import * as React from 'react';

import { ClickToScrollOffsets, useClickToScrollEffect } from '@tock/hooks';
import { DynamicTabs, DynamicTabsProps } from './DynamicTabs';

export interface DynamicTabsListNavigationProps<T>
  extends DynamicTabsProps<T>,
    ClickToScrollOffsets {
  afterScroll?(value: T, el: HTMLElement): void;
  className?: string;
  container?: React.ReactInstance | (() => React.ReactInstance | null) | null;
  /**
   * id selector when looking for a scroll target
   */
  selector?: (value: T) => string;
}

/**
 * DynamicTabsListNavigation is a is a drop in replacement for ScrollListNavigation/ScrollListNavigationItem using dynamic tabs to do scrolling navigation
 *
 * ```js
 * import { DynamicTabsListNavigation } from 'ui/tabs/DynamicTabsListNavigation';
 * ```
 */
export function DynamicTabsListNavigation<T = string>({
  afterScroll,
  container,
  offset,
  offsetMd,
  offsetSm,
  onChange,
  selector = (id) => `${id}-panel`,
  tabs,
  value,
  variant = 'tabs',
  ...rest
}: DynamicTabsListNavigationProps<T>) {
  const { handleChange } = useClickToScrollEffect<T>({
    afterScroll,
    container,
    offset,
    offsetMd,
    offsetSm,
    onChange,
    selector,
  });

  return <DynamicTabs variant={variant} tabs={tabs} onChange={handleChange} value={value} />;
}
